import { delay } from 'lodash';

import * as ConnectionActions from 'State/connection/actions';
import * as ConnectionSelectors from 'State/connection/selectors';
import { initializeNotifications } from 'Common/UI/state/actions';

import { env, ENV_PRODUCTION } from '../../env';
import {
  connecting,
  connected,
  connectionFailed,
  errored,
  disconnected,
  reconnect,
} from './state/actions';
import {
  SIGNALR_STATUS_CONNECTING,
  SIGNALR_STATUS_DISCONNECTING,
} from './state/types';
import {
  CONNECTION_RETRY_DELAY,
  CONNECTION_TRY_FOR_OFFLINE_MODE,
} from './helpers/constants';
import { getConnectionTries } from './state/selectors';

/**
 * Evaluate current status before connection attempt.
 *
 * @param int status
 */
export function signalRCanConnect(status = 0) {
  return (
    [SIGNALR_STATUS_DISCONNECTING, SIGNALR_STATUS_CONNECTING].indexOf(
      status
    ) === -1
  );
}

/**
 * Connect to the hubProxy
 *
 * @param {*} connection
 * @param {*} dispatch
 */
export function signalRStart(connection, dispatch, getState) {
  const currentState = getState();

  dispatch(connecting());
  // connect
  connection
    .start()
    .done(function () {
      if (env !== ENV_PRODUCTION) {
        console.log(`SignalR connected: ID=${connection.id}`);
      }
      const serverUp = ConnectionSelectors.getServerUp(currentState);
      if (!serverUp) {
        dispatch(ConnectionActions.setServerUp(true));
        dispatch(ConnectionActions.hideOfflinePrompt());
      }

      dispatch(connected());
      dispatch(getNotifications());
    })
    .fail(function (err) {
      if (env !== ENV_PRODUCTION) {
        console.warn('Could not connect');
      }
      dispatch(connectionFailed(err));

      // prompt offline mode
      const offlineMode = ConnectionSelectors.getOfflineMode(currentState);
      if (
        !offlineMode &&
        getConnectionTries(currentState) === CONNECTION_TRY_FOR_OFFLINE_MODE
      ) {
        dispatch(ConnectionActions.showOfflinePrompt());
      }

      dispatch(ConnectionActions.setServerUp(false));
    });
}

/**
 * Stop the transport connection.
 * Trigger dispatch event for resetting SignalRState.
 *
 *
 * @param {*} connection
 * @param {*} dispatch
 */
export function signalRStop(connection, dispatch) {
  if (env !== ENV_PRODUCTION) {
    console.log(`SignalR disconnected: ID=${connection.id}`);
  }
  connection.stop();
}

/**
 * For some reason not working.
 * Need to look into binding events to connection.
 *
 * @param {*} connection
 * @param {*} dispatch
 */
export function signalRTrackErrors(connection, dispatch, getState) {
  connection.error((error) => {
    if (error) {
      if (env !== ENV_PRODUCTION) {
        console.warn('SignalR error:', error);
      }

      dispatch(errored(error.message));
    }
  });
}

export function signalRDisconnected(connection, dispatch, getState) {
  connection.disconnected(() => {
    if (env !== ENV_PRODUCTION) {
      console.log(
        `SignalR disconnected: ID=${connection.id}. Will attempt to reconnect.`
      );
    }

    dispatch(ConnectionActions.setServerUp(false));

    const currentState = getState();

    //only show offline prompt if the application is not already in offline mode
    const offlineMode = ConnectionSelectors.getOfflineMode(currentState);
    if(!offlineMode){
      dispatch(ConnectionActions.showOfflinePrompt());
    }

    dispatch(disconnected());
    delay(() => dispatch(reconnect()), CONNECTION_RETRY_DELAY * 1000);
  });
}

export function signalRReconnected(connection, dispatch, getState) {
  connection.reconnected(() => {
    if (env !== ENV_PRODUCTION) {
      console.log(`SignalR reconnected: ID=${connection.id}`);
    }

    dispatch(ConnectionActions.setServerUp(true));
    dispatch(ConnectionActions.hideOfflinePrompt());
    dispatch(connected());
    dispatch(getNotifications());
  });
}

export function getNotifications() {
  return async (dispatch, getState, MPWWebApi) => {
    try {
      const response = await MPWWebApi.getGeneric('Notification/GetAllByUser');

      if (response) {
        dispatch(initializeNotifications(response.data.Data));
      }
    } catch (error) {
      if (env !== ENV_PRODUCTION) {
        console.log(
          `Error occured while fetching notifications: ${error.message}`
        );
      }
    }
  };
}



// WEBPACK FOOTER //
// ./src/api/SignalR/Connection.js